import type { Action } from 'redux';
import type { ThunkAction as _ThunkAction } from 'redux-thunk';

import type { StoreType } from '../reducers';

type PayloadInfo<Payload> = Payload extends undefined
	? Record<string, unknown>
	: { payload: Payload };

type ResultInfo<Result> = Result extends undefined ? Record<string, unknown> : { result: Result };

type PayloadAction<Type extends string, Payload> = Action<Type> & PayloadInfo<Payload>;

type StartAsyncAction<Type extends string, Payload> = PayloadAction<Type, Payload>;

type SuccessAsyncAction<Type extends string, Response, Payload> = PayloadAction<Type, Payload> & {
	result: Response;
};

type ErrorAsyncAction<Type extends string, Payload> = PayloadAction<Type, Payload> & {
	error: Error;
};

export enum AsyncActionStatus {
	INITIAL = 'initial',
	READY = 'ready',
	LOADING = 'loading',
	ERROR = 'error',
	NOTFOUND = 'notfound',
}

export type AsyncAction<
	Start extends string,
	Success extends string,
	Error extends string,
	Result,
	Payload = undefined,
> =
	| StartAsyncAction<Start, Payload>
	| SuccessAsyncAction<Success, Result, Payload>
	| ErrorAsyncAction<Error, Payload>;

// The extra thunk argument is `unknown` to match AppDispatch; no thunk reads it.
export type ThunkAction<AsyncActionDefinition extends Action<string>> = _ThunkAction<
	Promise<any>,
	StoreType,
	unknown,
	AsyncActionDefinition
>;

export type SyncAction<
	Type extends string,
	Result = undefined,
	Payload = undefined,
> = PayloadAction<Type, Payload> & ResultInfo<Result>;
